"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "../ui/badge";

interface Feature7Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  removePaddingBottom?: boolean;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  card?: {
    title?: string;
    content?: string;
    link?: string;
    image?: string;
    imageAltText?: string;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature7 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 7",
  removePaddingBottom = false,
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  card,
  attributes,
}: Feature7Props) => {

  const itemBackgroundColors = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      removePaddingBottom={removePaddingBottom}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature7"
      attributes={attributes}
    >

      <div className="container">
        <div className="flex flex-col justify-between gap-6 lg:flex-row md:pt-8 md:pb-24" data-aos="fade-up">
          {title && (
            <div className="lg:w-3/5 text-center md:text-left items-center md:items-start">
              {badge && (
                <Badge variant="outline" bold className="mb-6">
                  {badge}
                </Badge>
              )}
              <h2>
                {parse(title)}
              </h2>
            </div>
          )}
          {(content || hasButton) && (
            <div className="lg:w-2/5 flex flex-col gap-6">
              {content && (
                <div className="text-muted-foreground text-center md:text-left">
                  {parse(content)}
                </div>
              )}
              {hasButton && (
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex flex-col">
                    <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={buttonLink ?? ""}>
                        {buttonText}
                      </Link>
                    </Button>
                    {microCopy && (
                      <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                        {microCopy}
                      </p>
                    )}
                  </div>
                  {secondaryButton && (
                    <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={secondaryButtonLink ?? ""}>
                        {secondaryButtonText}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <Card className="mt-16 md:mt-20 border-none bg-transparent flex flex-col gap-0 p-0" data-aos="fade-up" data-aos-delay="100">
          <CardHeader className={cn(
            "p-6 bg-muted rounded-t-2xl",
            itemBackgroundColors,
          )}>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex flex-col gap-2 w-full">
                {card?.title && (
                  <h6>
                    {card.title}
                  </h6>
                )}
                {card?.content && (
                  <div className="text-muted-foreground">
                    {parse(card?.content ?? "")}
                  </div>
                )}
              </div>
              <div className="flex flex-col md:items-center justify-start md:justify-end">
                <Button asChild variant="outline" grayBorder size="iconLg">
                  <Link href={card?.link ?? ""}>
                    <span className="material-symbols-outlined">
                      arrow_forward
                    </span>
                  </Link>
                </Button>
              </div>
            </div>
          </CardHeader>
          {card?.image && (
            <CardContent className="p-0">
              <Image
                src={card?.image}
                alt={card?.imageAltText ?? ""}
                title={card?.imageAltText ?? ""}
                className="flex-1 w-full object-cover aspect-[660/372] rounded-b-2xl"
                width={1400}
                height={1400}
              />
            </CardContent>
          )}
        </Card>
      </div>
    </SectionWrapper>
  )
};

export { Feature7 };